// Copyright © 2026 Vale. All Rights Reserved.
// Unauthorized copying, modification, distribution, or use of this file,
// via any medium, is strictly prohibited. See LICENSE at the repository root.
import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthCallback } from "@/components/AuthCallback";
import { QuietSundayCelebration } from "@/components/QuietSundayCelebration";
import { InstallAppPrompt } from "@/components/InstallAppPrompt";
import { fbTrack } from "@/lib/pixel";
import Landing from "@/pages/Landing";
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Training = lazy(() => import("@/pages/Training"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const Plans = lazy(() => import("@/pages/Pricing"));
const Rhythm = lazy(() => import("@/pages/Rhythm"));
const Money = lazy(() => import("@/pages/Money"));
const Life = lazy(() => import("@/pages/Life"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Messages = lazy(() => import("@/pages/Messages"));
const Focus = lazy(() => import("@/pages/Focus"));
const Team = lazy(() => import("@/pages/Team"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminPnL = lazy(() => import("@/pages/AdminPnL"));
const AdKit = lazy(() => import("@/pages/AdKit"));
const Join = lazy(() => import("@/pages/Join"));
const Install = lazy(() => import("@/pages/Install"));
const Invite = lazy(() => import("@/pages/Invite"));
const Review = lazy(() => import("@/pages/Review"));
const MonthlyReel = lazy(() => import("@/pages/MonthlyReel"));
const Connections = lazy(() => import("@/pages/Connections"));
const Settings = lazy(() => import("@/pages/Settings"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const PaymentSuccess = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentCancel })));

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="page-loading">
    <div className="h-10 w-10 rounded-full border-2 border-[var(--calm)] border-t-transparent animate-spin" />
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function Router() {
  const location = useLocation();
  // Meta Pixel: fire PageView on every SPA route change (base snippet only fires
  // on first load). Skip the very first render — that was already tracked by index.html.
  useEffect(() => {
    if (!window.__valePixelFirstFired) { window.__valePixelFirstFired = true; return; }
    fbTrack("PageView");
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Spinner />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/pricing" element={<Plans />} />
      <Route path="/plans" element={<Navigate to="/pricing" replace />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
      <Route path="/rhythm" element={<Protected><Rhythm /></Protected>} />
      <Route path="/focus" element={<Protected><Focus /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/money" element={<Protected><Money /></Protected>} />
      <Route path="/life" element={<Protected><Life /></Protected>} />
      <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/invite" element={<Protected><Invite /></Protected>} />
      <Route path="/review" element={<Protected><Review /></Protected>} />
      <Route path="/review/month" element={<Protected><MonthlyReel /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/pnl" element={<Protected><AdminPnL /></Protected>} />
      <Route path="/adkit" element={<Protected><AdKit /></Protected>} />
      <Route path="/join" element={<Join />} />
      <Route path="/install" element={<Install />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/connections" element={<Protected><Connections /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/training" element={<Protected><Training /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

// "Privacy" opens the English policy for now; legal text needs a professional translator.
function Footer() {
  const { t } = useTranslation();
  return (
    <footer
      data-testid="app-copyright-footer"
      className="w-full text-center text-[11px] tracking-[0.12em] uppercase text-stone-400 py-4 select-none">
      <bdi dir="ltr">© 2026 Vale · Newcastle NSW</bdi> ·{" "}
      <a
        href="https://www.spicyburrito.com.au/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:text-stone-600"
        data-testid="footer-privacy-link"
      >
        {t("footer.privacy")}
      </a>
      {" · "}
      <a href="/terms" className="underline underline-offset-2 hover:text-stone-600" data-testid="footer-terms-link">{t("footer.terms")}</a>
      {" · "}
      <a
        href="https://www.facebook.com/profile.php?id=61595839852036"
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:text-stone-600"
        data-testid="footer-facebook-link"
      >
        Facebook
      </a>
      {" · "}
      <a
        href="https://www.instagram.com/spicyburrito.vale/"
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:text-stone-600"
        data-testid="footer-instagram-link"
      >
        Instagram
      </a>
      {" · "}{t("footer.rights")}
    </footer>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          <Router />
          <QuietSundayCelebration />
          <InstallAppPrompt />
          <Toaster position="top-right" />
          <Footer />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
